import { useCallback, useEffect, useRef, useState } from "react";

export interface UseResolvedSingleChatServerThreadIdParams {
  /** URL / host prop — wins when set. */
  initialThreadId?: string;
  /** Bumped when background generation finishes so we refetch without a URL id. */
  reloadKey: number;
  /** Server id paired with `reloadKey` (from background-generation handler). */
  reloadChatId: string | null;
}

/**
 * One resolved server chat id for single-chat mode.
 *
 * Order: `initialThreadId` → background reload id when `reloadKey` is non-zero
 * → id latched from `onChatCreated` (covers silent `history.replaceState` where
 * the parent prop does not update yet).
 */
export function useResolvedSingleChatServerThreadId({
  initialThreadId,
  reloadKey,
  reloadChatId,
}: UseResolvedSingleChatServerThreadIdParams): {
  serverThreadId: string | undefined;
  onServerThreadCreated: (id: string) => void;
} {
  const [runtimeLatchedId, setRuntimeLatchedId] = useState<string | null>(null);
  const prevInitialRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const prev = prevInitialRef.current;
    prevInitialRef.current = initialThreadId;

    if (initialThreadId) {
      setRuntimeLatchedId(null);
    } else if (prev !== undefined) {
      setRuntimeLatchedId(null);
    }
  }, [initialThreadId]);

  const onServerThreadCreated = useCallback((id: string) => {
    setRuntimeLatchedId(id);
  }, []);

  const serverThreadId =
    initialThreadId ||
    (reloadKey ? (reloadChatId ?? undefined) : undefined) ||
    (runtimeLatchedId ?? undefined) ||
    undefined;

  return { serverThreadId, onServerThreadCreated };
}
